import { isValidPhoneNumber } from 'libphonenumber-js'
import z from 'zod'
import getData from './getData'
import { findockFrequencyOptions } from '#shared/types'
import { t } from '#shared/utils/locales'

const { data: { donate: { amount_min }, locale } } = getData()

const ALLOWED_PAYMENT_METHODS = ['Vipps', 'AvtaleGiro', 'CreditCard'] as const
const PHONE_NOT_VALID_MESSAGE = t('validate.phone_not_valid')
const NAME_REQUIRED_MESSAGE = t('validate.name_required')
const FIRST_NAME_REQUIRED_MESSAGE = t('validate.first_name_required')
const LAST_NAME_REQUIRED_MESSAGE = t('validate.last_name_required')
const EMOJI_NOT_ALLOWED_MESSAGE = t('validate.emoji_not_allowed')
const EMAIL_REQUIRED_MESSAGE = t('validate.email_required')
const EMAIL_NOT_VALID_MESSAGE = t('validate.email_not_valid')
const AMOUNT_REQUIRED_MESSAGE = t('validate.amount_required')
const ONLY_NUMBERS_ALLOWED_MESSAGE = t('validate.only_numbers_allowed')
const DATE_NOT_VALID_MESSAGE = t('validate.date_not_valid')
const MIN_AMOUNT_MESSAGE = t('validate.min_amount', { amount: String(amount_min) || 'min_amount_missing' })
const NIN_REQUIRED_MESSAGE = t('validate.nin_required')

const z_name = z.string({ required_error: NAME_REQUIRED_MESSAGE }).min(1, NAME_REQUIRED_MESSAGE).regex(/^((?!\p{Extended_Pictographic}).)*$/u, EMOJI_NOT_ALLOWED_MESSAGE)
const z_first_name = z.string({ required_error: FIRST_NAME_REQUIRED_MESSAGE }).min(1, FIRST_NAME_REQUIRED_MESSAGE).regex(/^((?!\p{Extended_Pictographic}).)*$/u, EMOJI_NOT_ALLOWED_MESSAGE)
const z_last_name = z.string({ required_error: LAST_NAME_REQUIRED_MESSAGE }).min(1, LAST_NAME_REQUIRED_MESSAGE).regex(/^((?!\p{Extended_Pictographic}).)*$/u, EMOJI_NOT_ALLOWED_MESSAGE)
export const z_email = z.string({ required_error: EMAIL_REQUIRED_MESSAGE }).email(EMAIL_NOT_VALID_MESSAGE)
const z_phone = z.union([
  z.string().length(0, PHONE_NOT_VALID_MESSAGE),
  z.string().refine(phone => phone === undefined || isValidPhoneNumber(phone, locale), PHONE_NOT_VALID_MESSAGE),
]).optional()
// Why did I do it .nullable() again?w
const z_phone_required = z.string({ required_error: PHONE_NOT_VALID_MESSAGE }).refine(phone => phone !== undefined && isValidPhoneNumber(phone, locale), PHONE_NOT_VALID_MESSAGE)
const z_event_id = z.string().uuid()
export const z_sf_id = z.string().min(15).max(18)
// const z_mautic_id = z.number().min(1)
const z_date = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, { message: DATE_NOT_VALID_MESSAGE })
export const z_amount = z.number({ required_error: AMOUNT_REQUIRED_MESSAGE, invalid_type_error: ONLY_NUMBERS_ALLOWED_MESSAGE }).min(amount_min, MIN_AMOUNT_MESSAGE)
// const z_path = z.string().min(1).regex(/^\/.*/)
export const z_url = z.string().url()
const z_is_test = z.boolean().optional()
const z_origin = z.string()
const z_payment_methods = z.enum(ALLOWED_PAYMENT_METHODS)
const z_nin_no = z.string({ required_error: NIN_REQUIRED_MESSAGE }).min(11).max(11).regex(/^\d+$/, ONLY_NUMBERS_ALLOWED_MESSAGE)

export const meta_event = z.object({
  event_id: z_event_id,
  event_source_url: z.string().optional(),
  fbc: z.string().optional(),
  fbp: z.string().optional(),
  meta_event_time: z.number(),
  meta_pixel_id: z.string().optional(),
  meta_test_event_code: z.string().optional(),
})
export type Meta_event = z.output<typeof meta_event>

export const tracking_params = z.object({
  website_id: z.string().optional(),
  utm_source: z.string().optional(),
  utm_campaign: z.string().optional(),
  utm_medium: z.string().optional(),
  utm_content: z.string().optional(),
  utm_term: z.string().optional(),
})
export type Tracking_params = z.output<typeof tracking_params>

export const server_action_schema = z.object({
  is_test: z_is_test,

  add_action: z.boolean(),

  target_type: z.string(),
  target_id: z.string(),
  target_platform: z.string().optional(),

  message: z.string().optional(),

  action_type: z.string(),
  action_platform: z.string(),

  sf_campaign_id: z_sf_id.optional(),

  ...tracking_params.shape,
})

const email_temp = z.object({
  address: z.string().optional(),
  name: z.string().optional(),
})

export const server_action_confirm_schema = server_action_schema.extend({
  from_email: z.string().optional(),
  from_name: z.string().optional(),
  date: z.string().optional(),
  subject: z.string().optional(),
  body: z.string().optional(),
  to: z.array(email_temp).optional(),
  cc: z.array(email_temp).optional(),
  bcc: z.array(email_temp).optional(),
})
export type Server_action_confirm_schema = z.output<typeof server_action_confirm_schema>

export const basicInfoSchema = z.object({
  first_name: z_first_name,
  last_name: z_last_name,
  email: z_email,
  phone: z_phone,
})
export type BasicInfoSchema = z.output<typeof basicInfoSchema>

export const upgradeDonationFormScheme = basicInfoSchema.extend({
  amount: z_amount,
  phone: z_phone_required,
})
export type UpgradeDonationFormScheme = z.output<typeof upgradeDonationFormScheme>

export const upgradeDonationScheme = upgradeDonationFormScheme.extend({
  message: z.string(),
  type: z.string(),
  subject: z.string(),
})
export type UpgradeDonationScheme = z.output<typeof upgradeDonationScheme>

export const myPageFormSchema = basicInfoSchema.extend({
  phone: z_phone_required,
  type: z.string({ required_error: 'Velg en grunn for hvorfor du tar kontakt.' }),
  message: z.string({ required_error: 'Du må skrive kort om hvorfor du tar kontakt.' }),
  subject: z.string({ required_error: 'Du må skrive inn et emne.' }).optional(),
})
export type MyPageFormSchema = z.output<typeof myPageFormSchema>

export const myPageServerSchema = myPageFormSchema.extend({
  is_test: z_is_test,
})
export type MyPageServerSchema = z.output<typeof myPageServerSchema>

export const donateFormSchema = basicInfoSchema.extend({
  amount: z_amount,
  recurring: z.boolean(),
  frequency: z.enum(findockFrequencyOptions).optional(),
  start_date: z_date.optional(),
})
export type DonateFormSchema = z.output<typeof donateFormSchema>

export const donateFindockSchema = donateFormSchema.extend({
  is_test: z_is_test,
  success_url: z_url,
  failure_url: z_url,
  origin: z_origin.optional(),
  payment_method: z_payment_methods,
  sf_campaign_id: z_sf_id.optional(),
  ...tracking_params.shape,
})
export type DonateFindockSchema = z.output<typeof donateFindockSchema>

export const donateServerSchema = donateFindockSchema.extend({
  ...meta_event.shape,
})
export type DonateServerSchema = z.output<typeof donateServerSchema>

export const donatePhoneFormSchema = basicInfoSchema.extend({
  amount: z_amount,
  phone: z_phone_required,
  recurring: z.boolean(),
  frequency: z.enum(findockFrequencyOptions).optional(),
  start_date: z_date.optional(),
  sf_campaign_id: z_sf_id,
  phoner_first_name: z.string().min(1),
})
export type DonatePhoneFormSchema = z.output<typeof donatePhoneServerSchema>

export const donatePhoneServerSchema = donatePhoneFormSchema.extend({
  is_test: z_is_test,
  origin: z_origin,
  success_url: z_url,
  failure_url: z_url,
  payment_method: z_payment_methods,
  ...tracking_params.shape,
})
export type DonatePhoneServerSchema = z.output<typeof donatePhoneServerSchema>

export const donateSuccessSchema = basicInfoSchema.extend({
  is_test: z_is_test,
  amount: z_amount,
  recurring: z.boolean(),
  ...meta_event.shape,
})
export type DonateSuccessSchema = z.output<typeof donateSuccessSchema>

export const signFormSchema = basicInfoSchema.extend({
  newsletter: z.boolean(),
})
export type SignFormSchema = z.output<typeof signFormSchema>

export const signServerSchema = signFormSchema.extend({
  is_test: z.boolean().optional(),
  sf_campaign_id: z_sf_id,
  api_version: z.number().min(1).max(2).optional(),
  ...meta_event.shape,
  ...tracking_params.shape,
})

export type SignServerSchema = z.output<typeof signServerSchema>

export const createUserSchema = signServerSchema.extend({
  email_id: z.string(),
})

export type CreateUserSchema = z.output<typeof createUserSchema>

export const signServerBoilerplateSchema = z.object({
  name: z_name,
  email: z_email,
  phone: z_phone,
  newsletter: z.boolean(),
  is_test: z.boolean().optional(),
})

export type SignServerBoilerplateSchema = z.output<typeof signServerBoilerplateSchema>

export const eventSignUpForm = basicInfoSchema.extend({
  newsletter: z.boolean(),
  phone: z_phone_required,
})

export const eventSignUpServerSide = eventSignUpForm.extend({
  ...meta_event.shape,
  ...tracking_params.shape,
  is_test: z_is_test,
  sf_campaign_id: z_sf_id,
  age: z.number().optional(),
  want_food: z.boolean().optional(),
})

export type EventSignUpServerSide = z.output<typeof eventSignUpServerSide>

export const eventSchema = z.object({
  event_name: z.string().min(1),
  data: basicInfoSchema.extend({
    meta_event_time: z.number(),
    event_id: z.string(),
  }),
})

export type EventSchema = z.output<typeof eventSchema>

export const ninSchema = z.object({
  nin: z_nin_no,
  email: z_email,
  first_name: z_first_name.optional(),
  last_name: z_last_name.optional(),
  phone: z_phone.optional(),
  address_postal_code: z.string({ required_error: 'Postnummer er nødvendig.' }).min(4).max(4).optional(),
  address_city: z.string({ required_error: 'By er nødvendig.' }).optional(),
  address_street: z.string({ required_error: 'Adresse er nødvendig.' }).optional(),
  is_test: z_is_test,
})

export type NinSchema = z.output<typeof ninSchema>

export const metaCapiSchema = basicInfoSchema.extend(meta_event.shape)
export type MetaCapiSchema = z.output<typeof metaCapiSchema>

export const freshdeskTicketSchema = z.object({
  freshdesk_webhook: z.object({
    ticket_id: z.number(),
    ticket_subject: z.string(),
    ticket_status: z.string(),
    ticket_source: z.string(),
    ticket_ticket_type: z.string().nullable().optional(),
    ticket_contact_email: z.string().email(),
    ticket_description: z.string().optional(),
    // Optional fields
    ticket_tags: z.string().nullable().optional(),
    ticket_priority: z.string().optional(),
    ticket_contact_name: z.string().optional(),
    ticket_cf_salesforce_case_id: z.string().nullable().optional(),
  }),
})

export type FreshdeskTicketSchema = z.output<typeof freshdeskTicketSchema>
